import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { cx, css } from 'emotion';
import _ from 'lodash';
import DataExplorerService from 'services/DataExplorerService';
import ProcessService from 'services/ProcessService';
import ProcessUtils from 'utils/ProcessUtils';
import Constants from 'utils/Constants';
import InfoMessages from 'components/core/help/InfoMessages';
import ProcessField from 'components/core/fields/ProcessField';
import InfoIcon from 'components/core/InfoIcon';
import RIERadioGroup from 'components/core/editable/RIERadioGroup';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import { colorLabelBlue } from 'styles/base';

/**
 * Base class for process form field components that can be reused throughout the PCM.
 */
export default class TransformationTypeField extends ProcessField {

    static propTypes = Object.assign({
        coordinateSystemName: PropTypes.string,
        dimensionName: PropTypes.string,
        displayQuestion: PropTypes.bool, // things need to be handled differently if it's being included in the advanced transform parameter
        radioDisplay: PropTypes.bool  // display options as radio buttons vs select
    }, ProcessField.propTypes);

    static defaultProps = Object.assign({
        displayQuestion: true,
        radioDisplay: false
    }, ProcessField.defaultProps);

    getBaseDimensionPath = () => {
        return ['variable_retrieval', 'coordinate_systems', this.props.coordinateSystemName, 'dimensions', this.props.dimensionName];
    };

    getDimension = () => {
        return _.get(this.props.process, this.getBaseDimensionPath());
    };

    getTransformationTypeOptions = (omitPassthrough) => {
        const options = {};
        Constants.COORDINATE_SYSTEM_TRANSFORM_TYPES.forEach(type => {
            if (!omitPassthrough || type.value !== 'TRANS_PASSTHROUGH') {
                options[type.value] = type.label;
            }
        });

        return options;
    };

    getTransformationTypeOptionsNoPassthrough = () => {
        return this.getTransformationTypeOptions(true);
    };

    getTransforamtionTypeValue = () => {
        const dimension = this.getDimension();
        return dimension.data_transformation.transformation_type;
    };

    onTransformationTypeUpdated = (fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const exploredDatastreams = DataExplorerService.getExploredDatastreams(this.props.process.name);

        value = editor.getFormattedValue(value);
        editor.updateField([...this.getBaseDimensionPath(), 'data_transformation', 'transformation_type'], value);

        const dimension = this.getDimension();
        if (value == Constants.TRANSFORM_TYPE_PASSTHROUGH && dimension.gridding_method == Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID) {
            editor.updateGriddingMethod(this.props.coordinateSystemName, this.props.dimensionName, Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING);
        }

        editor.commit();
    };

    renderRadioDisplay = () => {
        // The radio element depends upon if we are running in wizard mode or not.
        // If we are running in wizard mode, then we are limiting the user so they can't enter bad values.
        // Specifically, if the dimension is currently passthrough, don't show the radio, just show a read-only
        // 'Passthrough' text value.
        // If the dimension is NOT passthrough, don't allow the user to select passthrough as an option.
        let options = this.getTransformationTypeOptions();
        const currentTransformationType = this.getTransforamtionTypeValue();
        if (ProcessUtils.isWizardMode(this.props.process.name)) {
            if (currentTransformationType === 'TRANS_PASSTHROUGH') {
                options = null;
            }  else {
                options = this.getTransformationTypeOptionsNoPassthrough();
            }
        }

        let radioGroup = '';
        if (options) {
            const radioPadding = this.props.displayQuestion ? 30 : 0;

            radioGroup = (
                <Flexbox className={css`margin-left:-10px;padding-left:${radioPadding}px;`}>
                    <RIERadioGroup
                        value={this.getTransforamtionTypeValue()}
                        fieldPath={[...this.getBaseDimensionPath(), 'data_transformation', 'transformation_type']}
                        options={options}
                        change={this.onTransformationTypeUpdated}
                        formDisplay
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                    />
                </Flexbox>
            );
        } else {
            radioGroup = <div style={{ marginLeft: '15px' }}>Passthrough</div>;
        }

        if (!this.props.displayQuestion) {
            // advanced transform parameter field already handles the question so we don't need it in that case
            return (radioGroup);
        }

        return (
            <Flexbox flexDirection={'column'} className={cx('transformation-type-field', css`margin-bottom: 15px;`)}>
                <Flexbox flexDirection="row" alignItems="center" marginBottom="15px">
                    <InfoIcon message={InfoMessages.transformationType} />
                    <div className={css`font-weight:900;`}>
                        What algorithm should be used to transform variable data dimensioned by
                        <span className={css`color:${colorLabelBlue}`}> {this.props.dimensionName}</span>?
                    </div>
                </Flexbox>
                {radioGroup}
            </Flexbox>
        );

    };

    renderSelectDisplay = () => {
        const value = Constants.getOptionById(this.getTransforamtionTypeValue(), Constants.COORDINATE_SYSTEM_TRANSFORM_TYPES);
        return (
            <Flexbox flexDirection="row" alignItems="flex-start" flexGrow={1} marginRight="5px">
                <InfoBoxLabel label="Transform Method" />
                <RIEComboBox
                    value={value}
                    options={Constants.COORDINATE_SYSTEM_TRANSFORM_TYPES}
                    change={this.onTransformationTypeUpdated}
                    fieldPath={[...this.getBaseDimensionPath(), 'data_transformation', 'transformation_type']}
                    isDisabled={!this.props.hasWritePermissions}
                    processId={this.props.process.name}
                />
            </Flexbox>
        );
    };

    render() {
        return this.props.radioDisplay ? this.renderRadioDisplay() : this.renderSelectDisplay();
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/TransformationTypeField.js